import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthProvider";
import { CompraUsuarioDto } from "@/dtos/CompraUsuarioDto";
import CompraRapidaService from "@/services/CompraRapidaService";
import { Copy, Newspaper, Share2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { labelPlural, labelPluralTitulosSemAcento, labelSingularLower } from "@/utils/copyTituloBilhete";

export interface CardComprasProps {
    compra: CompraUsuarioDto
}

export default function CardCompras({ compra }: CardComprasProps) {
    const { toast } = useToast();
    const navigate = useNavigate();
    const [compartilhando, setCompartilhando] = useState(false);
    const { usuario } = useAuth();
    const isAndroid = navigator.userAgent.toLowerCase().includes("android");
    const isInstagram = navigator.userAgent.toLowerCase().includes("instagram")

    async function handleVisualizarCompra() {
        navigate('/compra/detalhes/' + compra.IdCompra + "?token=" + encodeURIComponent(usuario?.TokenAcesso!));
    }

    // Função para converter URL de imagem em File
    async function urlToFile(url: string, filename: string): Promise<File> {
        const response = await fetch(url);
        const blob = await response.blob();
        return new File([blob], filename, { type: blob.type });
    }

    async function handleCompartilharTitulos() {
        try {
            setCompartilhando(true);

            const { Data: result } = await CompraRapidaService.buscarCompra(compra.IdCompra, usuario?.TokenAcesso!);

            if (!result.UrlCertificados || result.UrlCertificados.length === 0) {
                toast({
                    variant: 'destructive',
                    description: `Nenhum ${labelSingularLower()} encontrado para compartilhar`,
                    duration: 3000
                });
                return;
            }

            // Converter URLs em arquivos
            const files: File[] = [];
            for (let i = 0; i < result.UrlCertificados.length; i++) {
                const url = result.UrlCertificados[i];
                const filename = `titulo_${compra.NumeroPedido}_${i + 1}.jpeg`;
                const file = await urlToFile(url, filename);
                files.push(file);
            }

            // Verificar se o navegador suporta compartilhamento de arquivos
            if (navigator.canShare && navigator.canShare({ files })) {
                await navigator.share({
                    files,
                    title: `${labelPlural()} - Compra ${compra.NumeroPedido}`,
                    text: `${labelPlural()} da compra número ${compra.NumeroPedido} do sorteio ${compra.DataSorteio}`,
                });
            } else {
                // Fallback: baixar arquivos individualmente
                files.forEach((file) => {
                    const url = URL.createObjectURL(file);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = file.name;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                });
            }
        } catch (error: any) {
            if (error.toString().includes('AbortError')) {
                return;
            }

            console.error(`Erro ao compartilhar ${labelPlural().toLowerCase()}:`, error);
            toast({
                variant: 'destructive',
                description: `Erro ao compartilhar ${labelPlural().toLowerCase()}. Tente novamente.`,
                duration: 4000
            });
        } finally {
            setCompartilhando(false)
        }
    }


    return (
        <>
            <div className="border-4 rounded-md border-primary items-center w-[90%] md:min-w-64 md:basis-1/5">
                <div className="px-4 py-2">
                    <p className="text-primary-foreground">
                        <label className="font-semibold">Sorteio: {compra.DataSorteio} </label>
                    </p>

                    <p className="text-primary-foreground">
                        <label className="font-semibold"> Numero: </label> {compra.NumeroPedido}
                    </p>
                </div>
                <div className="flex flex-col gap-4 px-4 pb-5 items-start md:items-center">
                    <div>
                        <p> <label className="font-bold"> Qtd. {labelPluralTitulosSemAcento()}:     </label> {compra.QuantidadeCautelas} </p>
                        <p> <label className="font-bold"> Valor Total:            </label> R$ {compra.Valor} </p>
                        <p> <label className="font-bold"> Data da Compra:   </label> {compra.Data} </p>
                        <p> <label className="font-bold"> Situação:   </label> {compra.DetalheSituacao} </p>
                    </div>

                    {
                        compra.Boleto && (
                            <Button onClick={async () => {
                                await navigator.clipboard.writeText(compra.Boleto?.QrCodeText ?? '');
                                toast({
                                    variant: 'success',
                                    description: 'Código copiado com sucesso!',
                                    duration: 3000
                                })
                            }} className="gap-2">
                                <Copy />
                                Copiar Pix Copia e Cola
                            </Button>
                        )
                    }

                    {
                        compra.Situacao === 1 && (
                            <>
                                <Button className="gap-2" onClick={handleVisualizarCompra} >
                                    <Newspaper />
                                    <span>Visualizar {labelPlural()}</span>
                                </Button>
                                {
                                    isAndroid && isInstagram ? (
                                        <a
                                            href={`${window.location.origin}/compra/extrato-compras?token=${encodeURIComponent(usuario?.TokenAcesso!)}`}
                                            target="_blank"
                                            download
                                        >
                                            <Button type="button" className="gap-2" >
                                                <Share2 size={16} />
                                                {
                                                    compartilhando ? <span>Compartilhando...</span> : <span>Compartilhar {labelPlural()}</span>
                                                }
                                            </Button>
                                        </a>
                                    ) : (
                                        <Button disabled={compartilhando} className="gap-2" onClick={handleCompartilharTitulos} >
                                            <Share2 size={16} />
                                            {
                                                compartilhando ? <span>Compartilhando...</span> : <span>Compartilhar {labelPlural()}</span>
                                            }
                                        </Button>
                                    )
                                }
                            </>
                        )
                    }

                </div>
            </div>
        </>
    )
}